import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, Star, Volume2 } from 'lucide-react';
import { ChatTutorProps } from '../types';
import { CHAT_GREETINGS } from '../config/constants';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { apiClient } from '../utils/api';

const ChatTutor = ({ gameState, saveGameState }: ChatTutorProps) => {
  // State
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; text: string }>>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  // Hooks
  const { speak } = useTextToSpeech();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Initialize chat with greeting
  useEffect(() => {
    const randomGreeting = CHAT_GREETINGS[Math.floor(Math.random() * CHAT_GREETINGS.length)];
    setMessages([
      { role: 'assistant', text: randomGreeting }
    ]);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Award coins for chatting
  useEffect(() => {
    if (messageCount > 0 && messageCount % 5 === 0) {
      saveGameState({
        coins: gameState.coins + 3
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: '🎉 Amazing! You\'ve earned 3 coins for practicing! Keep it up!'
      }]);
    }
  }, [messageCount, gameState.coins, saveGameState]);

  // Handle sending message
  const handleSend = useCallback(async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setMessageCount(prev => prev + 1);

    const newHistory = [...messages, { role: 'user' as const, text: userMessage }];
    setMessages(newHistory);
    setSending(true);

    try {
      // Prepare API messages
      const apiMessages = [
        {
          role: 'system',
          content: 'You are a friendly, encouraging English teacher for Chinese primary school children named \'Panda Teacher\'. Your responses should be short (1-2 sentences), use simple vocabulary (CEFR A1/A2 level), include emojis, and occasionally provide a Chinese translation for difficult words in parentheses. Be very positive.'
        },
        ...newHistory.map(m => ({
          role: m.role,
          content: m.text
        }))
      ];

      const replyText = await apiClient.sendChatMessage(apiMessages);

      setMessages(prev => [...prev, { role: 'assistant', text: replyText }]);

      // Speak the response (remove Chinese characters for TTS)
      const englishText = replyText.replace(/[\u4e00-\u9fa5]/g, '');
      speak(englishText);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Oops, connection error! Try again. 😴'
      }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, messages, speak, apiClient]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-green-50 to-emerald-50">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-green-400 to-emerald-500 p-3 text-center text-white shadow-lg">
        <div className="flex items-center justify-center space-x-3">
          <div className="flex items-center bg-white/20 px-2 py-1 rounded-full">
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm font-bold ml-1">{messageCount} msgs</span>
          </div>
          <div className="flex items-center bg-yellow-400 px-2 py-1 rounded-full">
            <Star className="w-4 h-4" />
            <span className="text-sm font-bold ml-1">{gameState.coins} coins</span>
          </div>
        </div>
      </div>

      {/* Bonus Achievement */}
      {messageCount > 0 && messageCount % 5 === 0 && (
        <div className="bg-gradient-to-r from-yellow-300 to-orange-400 text-white px-3 py-2 text-center text-sm font-bold animate-pulse">
          🎉 Chat Master! +3 Coins Earned!
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={scrollRef}>
        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
            <div className={`max-w-[80%] rounded-2xl p-4 shadow-md text-lg transform transition-all hover:scale-105 ${
              message.role === 'user'
                ? 'bg-gradient-to-r from-sky-400 to-sky-600 text-white rounded-br-none'
                : 'bg-white text-gray-800 border-2 border-green-200 rounded-bl-none'
            }`}>
              {message.text}

              {message.role === 'assistant' && (
                <button
                  onClick={() => {
                    const englishText = message.text.replace(/[\u4e00-\u9fa5]/g, '');
                    speak(englishText);
                  }}
                  className="ml-2 inline-block opacity-60 hover:opacity-100 transition-all transform hover:scale-110"
                  title="Listen to this message"
                >
                  <Volume2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-gradient-to-r from-gray-200 to-gray-300 rounded-full px-4 py-3 text-gray-600 text-sm animate-pulse flex items-center space-x-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <span>Panda is thinking... 🐼</span>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white/90 backdrop-blur border-t-2 border-green-200 flex items-center space-x-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Tell Panda something..."
          className="flex-1 border-2 border-green-300 rounded-full px-4 py-3 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-lg"
          disabled={sending}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-400 disabled:to-gray-500 text-white p-3 rounded-full shadow-lg transition-all transform hover:scale-110 active:scale-95 disabled:transform-none"
          title="Send message"
        >
          <Send size={20} className={sending ? 'animate-spin' : ''} />
        </button>
      </div>
    </div>
  );
};

export default ChatTutor;